import React, { useEffect, useMemo, useState } from 'react';
import { useMatch } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../state/hooks';
import {
    fetchInboundOrders,
    fetchOutboundOrders,
} from '../state/features/areaDetailsSlice';
import { privateRoutes } from '../routes/appRouteList';
import { ApiStatus, EquipmentTypeCode } from '../utils/enums';
import { useFilters } from '../components/Filters/useFilters';
import BasePage from './Layouts/BasePage';
import PageFooter from './Layouts/PageFooter';
import PagePaper from './Layouts/PagePaper';
import PageContent from './Layouts/PageContent';
import PageHeader from './Layouts/PageHeader';
import AreaDetailsHeader from '../components/Details/Headers/AreaDetailsHeader';
import AreaDetails from '../components/Details/AreaDetails';
import OrderTable from '../components/Tables/OrderTable';

const Areas = (): React.JSX.Element => {
    const dispatch = useAppDispatch();
    const match = useMatch(privateRoutes.areas);
    const filters = useFilters();

    const areaName = match?.params.name;

    const [selectedEquipmentType, setSelectedEquipmentType] = useState(EquipmentTypeCode.VN);

    const inboundOrdersApiStatus = useAppSelector((state) => { return state.area.inboundOrdersApiStatus; });
    const inboundOrders = useAppSelector((state) => { return state.area.inboundOrders; });

    const outboundOrdersApiStatus = useAppSelector((state) => { return state.area.outboundOrdersApiStatus; });
    const outboundOrders = useAppSelector((state) => { return state.area.outboundOrders; });

    useEffect(() => {
        if (areaName) {
            dispatch(fetchInboundOrders({
                delivery_service_area_id: areaName,
                mri_freight_order_specified_eqpt_code: selectedEquipmentType,
                filter_criteria: filters,
            }));
        }
    }, [dispatch, areaName, selectedEquipmentType, filters]);

    useEffect(() => {
        if (areaName) {
            dispatch(fetchOutboundOrders({
                pickup_service_area_id: areaName,
                mri_freight_order_specified_eqpt_code: selectedEquipmentType,
                filter_criteria: filters,
            }));
        }
    }, [dispatch, areaName, selectedEquipmentType, filters]);

    const combinedAreaOrders = useMemo(() => {
        const combinedOrders = inboundOrders.concat(outboundOrders);

        const uniqueOrders = combinedOrders.filter((order, index, self) => {
            return index === self.findIndex((o) => {
                return (
                    o.freight_order_number === order.freight_order_number &&
                    o.reporting_source_code === order.reporting_source_code
                );
            });
        });

        return uniqueOrders;
    }, [inboundOrders, outboundOrders]);

    return (
        <BasePage>
            <PagePaper>
                <PageHeader>
                    <AreaDetailsHeader />
                </PageHeader>
                <PageContent>
                    <AreaDetails />
                </PageContent>
                <PageFooter>
                    <OrderTable
                        isLoading={
                            inboundOrdersApiStatus === ApiStatus.Idle || inboundOrdersApiStatus === ApiStatus.Loading ||
                            outboundOrdersApiStatus === ApiStatus.Idle || outboundOrdersApiStatus === ApiStatus.Loading
                        }
                        orders={combinedAreaOrders}
                        orderCount={combinedAreaOrders.length}
                        equipmentType={selectedEquipmentType}
                        handleEquipmentTypeChange={(newEquipmentType): void => {
                            setSelectedEquipmentType(newEquipmentType);
                        }}
                    />
                </PageFooter>
            </PagePaper>
        </BasePage>
    );
};

export default Areas;
